import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
	Alert,
	AlertColor,
	Button,
	FormControl,
	FormHelperText,
	Grid,
	InputLabel,
	MenuItem,
	Select,
	Snackbar,
	TextField,
	Typography,
} from '@mui/material';
import 'react-credit-cards/es/styles-compiled.css';
import { IBranchOfficeToCashPayment, ICoin, ICreateOrderCash, IFilledOrderCashForm } from 'features/payment/types';
import { extractErrorMessage } from 'features/quotation/helpers';
import { findContactDataByDeal } from 'services';
import { AutocompleteContactData } from 'features/quotation/types';
import { createCashOrder, getBranchOfficesAllowedToCreateCashOrder } from 'features/payment/services';
import { ConfirmationModal } from 'features/common/components/ConfirmationModal/ConfirmationModal';
import { checkShowErrorMessage } from '../../../common/helpers';
import BitrixDealOrCaseSwitch from '../../../common/components/BitrixDealOrCaseSwitch';
import BitrixDealOrCaseUserInfoConfirm from '../../../common/components/BitrixDealOrCaseUserInfoConfirm';
import { getNimbuxCaseSummary } from '../../../common/services';
import { isDlocalBranchOficeId } from '../../helpers';
import { trigger } from 'swr';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import { isAdminOrSuperAdmin } from '../../../auth/helpers';

interface ShowAlertState {
	show: boolean;
	severity: AlertColor;
	message: string;
}

interface ICrmTicketContactCheck {
	message: string[] | string;
	success: boolean;
}

export const CashOrderForm = (): JSX.Element => {
	const { auth } = useSelector((state: RootState) => state);
	const {
		control,
		reset,
		setValue,
		getValues,
		watch,
		formState: { errors, isValid },
	} = useForm<IFilledOrderCashForm>({
		mode: 'all',
		defaultValues: {
			branchOfficeId: '',
			crmTicket: 0,
			coinId: '',
			amount: undefined,
			fee: undefined,
			observations: '',
		},
	});

	const [coinsList, setCoinsList] = useState<ICoin[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const [branchOfficesList, setBranchOfficesList] = useState<IBranchOfficeToCashPayment[]>([]);
	const defaultAlertState: ShowAlertState = { show: false, severity: 'success', message: '' };
	const [alert, setAlert] = React.useState<ShowAlertState>(defaultAlertState);
	const [open, setOpen] = useState(false);
	const [contactCrmTicketCheck, setContactCrmTicketCheck] = useState<ICrmTicketContactCheck | null>(null);
	const [isBitrixDeal, setIsBitrixDeal] = useState<boolean>(false);
	const [isPrePopulated, setIsPrePopulated] = useState<boolean>(false);
	const handleOpen = () => setOpen(true);

	const branchOfficeId = watch('branchOfficeId');
	const isDLocalPayment = isDlocalBranchOficeId(Number(branchOfficeId));

	// Obtener parámetros de la URL
	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		const dealId = params.get('dealId');
		const caseId = params.get('caseId');

		if (dealId) {
			setIsBitrixDeal(true);
			setIsPrePopulated(true);
			setTimeout(() => {
				setValue('crmTicket', parseInt(dealId), { shouldValidate: true, shouldDirty: true });
				console.log('After setValue, form value:', getValues('crmTicket'));
				findContactOfDeal();
			}, 100);
		} else if (caseId) {
			setIsBitrixDeal(false);
			setIsPrePopulated(true);
			setTimeout(() => {
				setValue('crmTicket', parseInt(caseId), { shouldValidate: true, shouldDirty: true });
				findNimbuxCaseSummary();
			}, 100);
		}
	}, []);

	//Obtengo la lista de Sucursales permitidas para cash payment
	const loadBranchOffices = async () => {
		try {
			setLoading(true);
			const branchOffices = (await getBranchOfficesAllowedToCreateCashOrder()).data;
			const isAdminOrSuperAdminValue = isAdminOrSuperAdmin(auth.user);
			if (isAdminOrSuperAdminValue) {
				setBranchOfficesList(branchOffices);
			} else {
				setBranchOfficesList(branchOffices.filter((x) => !x.onlyAdmin));
			}

			setLoading(false);
		} catch (error: any) {
			setLoading(false);
			setAlert({ show: true, severity: 'success', message: 'No se pudo cargar las sucursales' });
		}
	};
	const parseInputToNumber = (input: string): number => {
		return parseInt(input);
	};

	const isValidContactDealCheck = (): boolean => contactCrmTicketCheck != null && contactCrmTicketCheck.success;

	const canProcessOrder = (): boolean => !loading && isValid && isValidContactDealCheck();

	//obtengo los datos del Deal ID
	const findContactOfDeal = async () => {
		const crmTicket = getValues('crmTicket');
		if (crmTicket) {
			try {
				setLoading(true);
				const response = await findContactDataByDeal(Number(crmTicket));
				setContactCrmTicketCheck({ message: convertContactDataToMessage(response.data), success: true });
				setLoading(false);
			} catch (error: any) {
				if (error.response.status === 404) {
					setContactCrmTicketCheck({ message: 'El deal no tiene contacto asociado', success: true });
					setLoading(false);
				} else {
					setContactCrmTicketCheck({ message: 'No existe un deal con ese ID', success: false });
					setLoading(false);
				}
			}
		}
	};

	const findNimbuxCaseSummary = async () => {
		const crmTicket = getValues('crmTicket');
		if (crmTicket) {
			try {
				setLoading(true);
				const response = await getNimbuxCaseSummary(Number(crmTicket));
				if (!response.data?.client) {
					setContactCrmTicketCheck({
						message: 'El caso no tiene cliente asociado, por favor asocie un cliente al caso',
						success: false,
					});
				} else {
					const client = response.data.client;
					const autocompleteContactData = {
						name: `${client.firstName ?? ''} ${client.lastName ?? ''}`.trim(),
						email: client.email,
						phone: client.completePhoneNumber,
					} as AutocompleteContactData;
					setContactCrmTicketCheck({ message: convertContactDataToMessage(autocompleteContactData), success: true });
				}

				setLoading(false);
			} catch (error: any) {
				const errorMessage = extractErrorMessage(error, 'Ocurrió un error al buscar el caso');
				setContactCrmTicketCheck({ message: errorMessage, success: false });
				setLoading(false);
			}
		}
	};
	const handleCrmTicket = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			if (isBitrixDeal) {
				findContactOfDeal();
			} else {
				findNimbuxCaseSummary();
			}
		}
	};

	const onSubmit = async () => {
		try {
			setLoading(true);
			const values = getValues();
			const orderToSend = {
				branchOfficeId: Number(values.branchOfficeId),
				crmTicket: Number(values.crmTicket),
				isBitrixDeal: isBitrixDeal,
				coinId: Number(values.coinId),
				amount: Number(values.amount),
				...(values.observations && values.observations != '' && { observations: values.observations }),
				...(isDLocalPayment && { fee: Number(values.fee) }),
			} as ICreateOrderCash;
			await createCashOrder(orderToSend);
			setLoading(false);
			setOpen(false);
			setAlert({
				show: true,
				severity: 'success',
				message: 'Orden de pago cash creada exitosamente',
			});
			reset();
			setContactCrmTicketCheck(null);
		} catch (error: any) {
			setAlert({
				show: true,
				severity: 'error',
				message: extractErrorMessage(error, 'Ocurrió un error al crear la orden'),
			});
			setLoading(false);
		}
	};

	const convertContactDataToMessage = (contact: AutocompleteContactData): string[] => {
		const stringMessageChunk = [];
		stringMessageChunk.push(`Nombre: ${contact.name}`);
		if (contact.email) stringMessageChunk.push(`Correo: ${contact.email}`);
		if (contact.phone) stringMessageChunk.push(`Teléfono: ${contact.phone}`);
		return stringMessageChunk;
	};

	useEffect(() => {
		loadBranchOffices();
	}, []);

	useEffect(() => {
		setContactCrmTicketCheck(null);
		if (!isPrePopulated) {
			setValue('crmTicket', 0);
		}
	}, [isBitrixDeal]);

	return (
		<>
			<form>
				<Grid container gap={2}>
					<Grid item xs={12}>
						<Controller
							name="branchOfficeId"
							control={control}
							rules={{
								required: {
									value: true,
									message: 'Por favor seleccione sucursal',
								},
							}}
							render={({ field, fieldState: { error } }) => (
								<FormControl required={true} sx={{ width: '100%', height: '100%' }}>
									<InputLabel>Sucursal</InputLabel>
									<Select
										labelId="branchOfficeId"
										label="Sucursal"
										required
										disabled={loading}
										sx={{ width: '100%' }}
										{...field}
										onChange={(event) => {
											const branchOfficeId = parseInputToNumber(event.target.value);
											field.onChange(branchOfficeId);
											const coinsAllowed = branchOfficesList.find((x) => x.id == branchOfficeId)?.coinAllowed || [];
											setCoinsList(coinsAllowed);
											setValue('coinId', '');
											trigger('coinId');
										}}
										value={field.value || ''}
									>
										{branchOfficesList.map((branchOffice) => (
											<MenuItem value={branchOffice.id} key={branchOffice.id}>
												{branchOffice.name}
											</MenuItem>
										))}
									</Select>
									{error && <FormHelperText sx={{ color: 'red' }}>{error.message}</FormHelperText>}
								</FormControl>
							)}
						/>
					</Grid>
					<Grid item xs={12}>
						<BitrixDealOrCaseSwitch
							isBitrixDeal={isBitrixDeal}
							onChange={() => !isPrePopulated && setIsBitrixDeal(!isBitrixDeal)}
							disabled={isPrePopulated}
						/>
					</Grid>
					<Grid item xs={12}>
						<Controller
							name="crmTicket"
							control={control}
							rules={{
								required: { value: true, message: 'Este campo es requerido' },
								min: {
									value: 1,
									message: 'El valor debe ser mayor que 0',
								},
								pattern: {
									value: /^[0-9]+$/,
									message: 'El valor debe ser numérico',
								},
							}}
							render={({ field }) => (
								<TextField
									variant="outlined"
									required
									fullWidth
									label={isBitrixDeal ? 'Deal ID' : 'Caso'}
									type="number"
									disabled={loading || isPrePopulated}
									onKeyPress={handleCrmTicket}
									helperText={
										!isPrePopulated ? `Inserte el ${isBitrixDeal ? 'Deal ID' : 'Caso'} y presione enter` : undefined
									}
									error={Boolean(errors.crmTicket)}
									{...field}
									value={field.value || 0}
								/>
							)}
						/>
						<BitrixDealOrCaseUserInfoConfirm isBitrixDeal={isBitrixDeal} />
					</Grid>
					{contactCrmTicketCheck && (
						<Typography color={contactCrmTicketCheck.success ? 'green' : 'red'} fontSize={12}>
							{' '}
							{Array.isArray(contactCrmTicketCheck.message)
								? contactCrmTicketCheck.message.map((message) => {
										return <p key={message}>{message}</p>;
								  })
								: contactCrmTicketCheck.message}{' '}
						</Typography>
					)}
					<Grid item xs={12}>
						<Controller
							name="coinId"
							control={control}
							rules={{
								required: {
									value: true,
									message: 'Por favor seleccione una moneda',
								},
							}}
							render={({ field, fieldState: { error } }) => (
								<FormControl required={true} sx={{ width: '100%', height: '100%' }}>
									<InputLabel id="currency-label">Moneda</InputLabel>
									<Select
										labelId="currency-label"
										id="coinId-select"
										label="Moneda"
										disabled={!coinsList.length || loading}
										required
										sx={{ width: '100%' }}
										{...field}
									>
										{coinsList.map((currency) => (
											<MenuItem key={currency.id} value={currency.id}>
												{currency.code}
											</MenuItem>
										))}
									</Select>
									{error && <FormHelperText sx={{ color: 'red' }}>{error.message}</FormHelperText>}
								</FormControl>
							)}
						/>
					</Grid>
					<Grid item xs={12}>
						<Controller
							name="amount"
							control={control}
							rules={{
								required: { value: true, message: 'Este campo es requerido' },
								validate: {
									notZero: (value) => value !== 0 || 'El monto debe ser distinto de 0',
								},
								pattern: {
									value: /^-?(0|[1-9]\d*)(\.\d+)?$/,
									message: 'El valor debe ser numérico',
								},
							}}
							render={({ field }) => (
								<TextField
									variant="outlined"
									required
									fullWidth
									label="Monto"
									type="number"
									disabled={loading}
									helperText={checkShowErrorMessage(Boolean(errors.amount), errors.amount?.message)}
									error={Boolean(errors.amount)}
									{...field}
								/>
							)}
						/>
					</Grid>
					{isDLocalPayment && (
						<Grid item xs={12}>
							<Controller
								name="fee"
								control={control}
								rules={{
									required: { value: true, message: 'Este campo es requerido' },
									validate: {
										positiveInteger: (value) =>
											Number.isInteger(Number(value)) && Number(value) > 0
												? true
												: 'Debe ser un número entero mayor a 0',
									},
								}}
								render={({ field }) => (
									<TextField
										variant="outlined"
										required
										fullWidth
										label="Cuotas"
										type="number"
										inputProps={{ step: 1, min: 1 }} // 👈 fuerza pasos enteros en el input
										disabled={loading}
										helperText={checkShowErrorMessage(Boolean(errors.fee), errors.fee?.message)}
										error={Boolean(errors.fee)}
										{...field}
									/>
								)}
							/>
						</Grid>
					)}
					<Grid item xs={12}>
						<Controller
							name="observations"
							control={control}
							render={({ field }) => (
								<TextField
									fullWidth
									label="Observaciones"
									variant="outlined"
									multiline
									rows={5}
									disabled={loading}
									{...field}
								/>
							)}
						/>
					</Grid>
					<Grid item xs={12} display={'flex'} justifyContent="flex-end">
						<Button variant="contained" onClick={handleOpen} disabled={!canProcessOrder()} color="success" fullWidth>
							Agregar orden de Pago
						</Button>
						<ConfirmationModal
							open={open}
							handleClose={() => setOpen(false)}
							handleAccept={onSubmit}
							message="Confirmar orden de Pago Cash"
						/>
					</Grid>
				</Grid>
			</form>
			<Snackbar open={alert.show} autoHideDuration={1500} onClose={() => setAlert(defaultAlertState)}>
				<Alert variant="filled" severity={alert.severity}>
					{alert.message}
				</Alert>
			</Snackbar>
		</>
	);
};
